<template>
  <div>
    <template v-if="!report_universal_categories && !loadTimeout">
      <div>
        <b-skeleton animation="wave" width="85%" />
        <b-skeleton animation="wave" width="55%" />
        <b-skeleton animation="wave" width="70%" />
      </div>
    </template>
    <template
      v-else-if="
        (!report_universal_categories && loadTimeout) ||
        (report_universal_categories && report_universal_categories.length === 0)
      "
    >
      <div class="my-5">
        {{ lang === 'KOR' ? 'GRI 지표를 선택해주세요.' : 'Please select GRI indicators.' }}
      </div>
    </template>
    <template v-else>
      <div
        v-for="category in report_universal_categories"
        :key="category.category_id"
        class="new-page w-100"
      >
        <h2 :id="`univ-category-title-${category.category_id}`">
          GRI {{ category.category_id }}: {{ category.category_name_e }}
        </h2>

        <b-table-simple table-class="sb-write-table mb-5 w-100">
          <b-thead>
            <b-tr>
              <b-td class="custom-thead-td">
                {{ lang === 'KOR' ? '구분' : 'Category' }}
              </b-td>
              <b-td class="custom-thead-td text-no-wrap">
                {{ lang === 'KOR' ? 'GRI 번호' : 'GRI Index' }}
              </b-td>
              <b-td class="custom-thead-td">
                {{ lang === 'KOR' ? '지표명' : 'Disclosure' }}
              </b-td>
              <b-td class="custom-thead-td"> Contents </b-td>
            </b-tr>
          </b-thead>
          <b-tbody>
            <template
              v-for="(subtopic, subtopicIndex) in getUniqueSubtopicsByCategoryIds(
                category.category_id
              )"
            >
              <b-tr
                v-for="(disclosure, disclosureIndex) in getGRIContentsBySubtopicCodes(
                  subtopic.subtopic_code
                )"
                :id="!print_mode ? 'w' + disclosure.gri_code : ''"
                :key="disclosure.disclosure_id"
                class="no-page-break-inside"
              >
                <b-td
                  v-if="shouldShowRow(subtopicIndex, disclosureIndex)"
                  :id="`univ-${category.category_id}-${subtopic.subtopic_code}`"
                  :rowspan="calculateRowspan(subtopic.subtopic_code)"
                >
                  {{ lang === 'KOR' ? disclosure.subtopic_name : disclosure.subtopic_name_e }}
                </b-td>
                <b-td class="text-no-wrap">{{ disclosure.gri_code }}</b-td>
                <b-td>
                  {{ lang === 'KOR' ? disclosure.indicator_name : disclosure.indicator_name_e }}
                </b-td>
                <b-td>
                  <div class="direct-report-content">
                    <p v-if="disclosure.direct_report">
                      <span
                        v-html="
                          sanitizeHtml(
                            lang === 'KOR' ? disclosure.direct_report : disclosure.direct_report_e
                          )
                        "
                      ></span>
                    </p>
                  </div>
                  <div v-for="table_id in disclosure.data_tables" :key="table_id" class="mb-1">
                    <template v-if="!table_id || getTableTitle(table_id) === ''">
                      <b-skeleton width="85%" />
                    </template>

                    <template v-else-if="!print_mode">
                      <a :href="getTableLink(table_id)" class="cursor" :style="main_color">
                        <b-icon icon="table" />
                        {{ getTableTitle(table_id) }}
                      </a>
                    </template>
                    <template v-else>
                      <DataTable
                        :print_mode="print_mode"
                        :table_mode="'accordion'"
                        :data_table_id="table_id"
                        :modal_table="false"
                        :lazy_load="false"
                        :table_header_color="main_color"
                        :table_header_font_color="'white'"
                      />
                    </template>
                  </div>

                  <div v-for="document_id in disclosure.documents" :key="document_id" class="mb-1">
                    <template v-if="!document_id || getDocumentTitle(document_id) === ''">
                      <b-skeleton width="85%" />
                    </template>
                    <template v-else>
                      <a :href="getDocumentLink2(document_id)" :style="main_color">
                        <span>
                          <b-icon :icon="getIconByType(document_id)" />
                          {{ getDocumentTitle(document_id) }}
                        </span>
                      </a>
                    </template>
                  </div>

                  <div
                    v-if="
                      disclosure.gri_code === '3-2' &&
                      selected_indicator &&
                      selected_indicator.custom_topic_indicator.length > 0
                    "
                    class="mt-5"
                  >
                    <b-table-simple
                      responsive
                      table-class="sb-write-table mb-5"
                      :borderless="!show_table_line"
                      :outlined="show_table_outline"
                    >
                      <b-thead>
                        <b-tr>
                          <b-td class="custom-thead-td" :style="table_header_style">
                            {{ lang !== 'ENG' ? '우선순위' : 'Ranking' }}
                          </b-td>
                          <b-td class="custom-thead-td" :style="table_header_style">
                            {{ lang !== 'ENG' ? '중요 주제명' : 'Topic' }}
                          </b-td>
                          <b-td class="custom-thead-td" :style="table_header_style">
                            {{
                              lang !== 'ENG' ? '중요 주제와 연관된 GRI 지표' : 'GRI Topic Standards'
                            }}
                          </b-td>
                        </b-tr>
                      </b-thead>
                      <b-tbody>
                        <b-tr
                          v-for="(item, idx) in selected_indicator.custom_topic_indicator"
                          :key="idx"
                        >
                          <b-td>
                            {{ idx + 1 }}
                          </b-td>
                          <b-td>
                            {{
                              lang !== 'ENG'
                                ? item.topic_name
                                : item.topic_name_em
                                ? item.topic_name_em
                                : item.topic_name_ea
                            }}
                          </b-td>
                          <b-td>
                            <span
                              v-for="gri_code in sort_indicator_arr(item.selected_cm_gri_code_list)"
                              :key="gri_code"
                            >
                              <a :href="`#w${gri_code}`" :style="main_color"> {{ gri_code }} </a
                              >&nbsp;
                            </span>

                            <span
                              v-if="
                                item.selected_cm_gri_code_list &&
                                item.selected_cm_gri_code_list.length === 0
                              "
                            >
                              Non-GRI
                            </span>
                          </b-td>
                        </b-tr>
                      </b-tbody>
                    </b-table-simple>
                  </div>
                </b-td>
              </b-tr>
            </template>
          </b-tbody>
        </b-table-simple>
      </div>

      <!-- gri 200 ~ 400 -->
      <h2 id="Topic Standards" class="new-page">Topic Standards</h2>
      <b-table-simple responsive table-class="sb-write-table">
        <b-thead>
          <b-tr>
            <b-td class="custom-thead-td">
              {{ lang === 'KOR' ? '구분' : 'Category' }}
            </b-td>
            <b-td class="custom-thead-td text-no-wrap">
              {{ lang === 'KOR' ? 'GRI 번호' : 'GRI Index' }}
            </b-td>
            <b-td class="custom-thead-td">
              {{ lang === 'KOR' ? '지표명' : 'Disclosure' }}
            </b-td>
            <b-td class="custom-thead-td"> Contents </b-td>
          </b-tr>
        </b-thead>

        <b-tbody>
          <template v-for="category in report_topic_specific_categories">
            <template v-for="topic in getUniqueTopicsByCategoryIds(category.category_id)">
              <b-tr
                v-for="(disclosure, disclosureIndex) in getGRIContentsByTopicIds(topic.topic_id)"
                :id="!print_mode ? 'w' + disclosure.gri_code : ''"
                :key="disclosure.disclosure_id"
              >
                <b-td
                  v-if="disclosureIndex === 0"
                  :id="`spec-${category.category_id}-${topic.topic_id}`"
                  :rowspan="getGRIContentsByTopicIds(topic.topic_id).length"
                >
                  {{ lang === 'KOR' ? topic.topic_name : topic.topic_name_e }}
                </b-td>
                <b-td class="text-no-wrap">{{ disclosure.gri_code }}</b-td>
                <b-td>
                  {{ lang === 'KOR' ? disclosure.indicator_name : disclosure.indicator_name_e }}
                </b-td>
                <b-td>
                  <div class="direct-report-content">
                    <p v-if="lang === 'KOR' && disclosure.direct_report">
                      <span v-html="sanitizeHtml(disclosure.direct_report)"></span>
                    </p>
                    <p v-else-if="lang !== 'KOR' && disclosure.direct_report_e">
                      <span v-html="sanitizeHtml(disclosure.direct_report_e)"></span>
                    </p>
                  </div>
                  <div v-for="table_id in disclosure.data_tables" :key="table_id" class="mb-1">
                    <template v-if="!table_id || getTableTitle(table_id) === ''">
                      <b-skeleton width="85%" />
                    </template>
                    <template v-else-if="!print_mode">
                      <a
                        :href="getTableLink(table_id)"
                        target="_blank"
                        class="cursor"
                        :style="main_color"
                      >
                        <b-icon icon="table" />
                        {{ getTableTitle(table_id) }}
                      </a>
                    </template>
                    <template v-else>
                      <DataTable
                        :print_mode="print_mode"
                        :table_mode="'accordion'"
                        :data_table_id="table_id"
                        :modal_table="false"
                        :lazy_load="false"
                        :table_style="'basic_table'"
                        :table_header_color="main_color"
                        :table_header_font_color="'white'"
                      />
                    </template>
                  </div>

                  <div v-for="document_id in disclosure.documents" :key="document_id" class="mb-2">
                    <template v-if="!document_id || getDocumentTitle(document_id) === ''">
                      <b-skeleton width="85%" />
                    </template>
                    <template v-else>
                      <a :href="getDocumentLink2(document_id)" target="_blank" :style="main_color">
                        <span>
                          <b-icon :icon="getIconByType(document_id)" />
                          {{ getDocumentTitle(document_id) }}
                        </span>
                      </a>
                    </template>
                  </div>
                </b-td>
              </b-tr>
            </template>
          </template>
        </b-tbody>
      </b-table-simple>
    </template>
  </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex';
import DataTable from '@/components/report_preview/gri2021_viewer/viewer_components/DataTable.vue';
import { gri_mixin } from '@/components/writing_report/write_report_subs/gri_mixin.js';
import { sort_indicator_arr } from '../../../util';
import { sanitizeHtml } from '@/components/sanitize.js';
export default {
  name: 'GriSbLinkedTable',
  mixins: [gri_mixin],
  props: {
    modal_table: { type: Boolean, default: false },
    reload: { type: Boolean, default: false },
    print_mode: { type: Boolean, default: false },
    business_case_mode: { type: String, default: 'popup' },
    table_mode: { type: String, default: 'accordion' },
    material_style_option: { type: String, default: 'outline' },

    table_style: { type: String, default: 'basic_table' },
    show_table_line: { type: Boolean, default: true },
    show_table_outline: { type: Boolean, default: false },
    table_header_color: { type: String, default: '#dedede' },
    table_header_font_color: { type: String, default: '#fffff' }
  },
  data() {
    return {
      loadTimeout: false,
      timerId: null,
      selected_indicator: null
    };
  },
  computed: {
    ...mapState('report_list', ['lang']),
    ...mapGetters('report_list', ['selected_report', 'current_report_code']),
    ...mapState('documents', ['document_list']),
    ...mapState('data_tables', ['table_list']),
    ...mapGetters('gri2021_report_view', [
      'report_universal_categories',
      'report_topic_specific_categories'
    ]),
    table_header_style() {
      return `${this.table_style}` === 'custom_table'
        ? `color: ${this.table_header_font_color}; background-color: ${this.table_header_color}`
        : '';
    },
    main_color() {
      return `color:` + this.selected_report?.main_color;
    },
    is_published() {
      return this.selected_report?.state === 4;
    }
  },
  components: {
    DataTable
  },
  watch: {
    report_universal_categories(newVal) {
      if (newVal != null && this.timerId) {
        clearTimeout(this.timerId);
      }
    }
  },
  mounted() {
    this.timerId = setTimeout(() => {
      if (!this.report_universal_categories) {
        this.loadTimeout = true;
      }
    }, 5000);
    this.selected_gri_code_list();
  },

  methods: {
    sort_indicator_arr,
    sanitizeHtml,
    getTableLink(table_id) {
      return (
        this.$store.state.frontend_host +
        `/form_gallery/?type=dtable&id=${table_id}${this.lang === 'ENG' ? '&lang=ENG' : ''}`
      );
    },
    getDocumentLink2(document_id) {
      const document = this.document_list.find(i => i.document_id === document_id);
      return this.getDocumentLink(document);
    },
    shouldShowRow(subtopicIndex, disclosureIndex) {
      return disclosureIndex === 0;
    },
    calculateRowspan(subtopicCode) {
      const disclosures = this.getGRIContentsBySubtopicCodes(subtopicCode);
      return disclosures.length;
    },

    getIconByType(document_id) {
      const doc = this.document_list.find(i => i.document_id === document_id);
      return this.getDocumentIcon(doc?.type);
    },

    getTableTitle(table_id) {
      const table = this.table_list.find(i => i.table_id === table_id);
      return table ? (this.lang === 'KOR' ? table.title : table.title_e) : '';
    },
    getDocumentTitle(document_id) {
      const document = this.document_list.find(i => i.document_id === document_id);
      return document ? (this.lang === 'KOR' ? document.title : document.title_e) : '';
    },
    async selected_gri_code_list() {
      const response = await this.$publicApi.get(
        '/get_selected_gri_indicator_code?rc=' + this.current_report_code
      );
      if (response.data.code) {
        this.selected_indicator = response.data.result;
      }
    }
  }
};
</script>

<style></style>
